import React, { useCallback, useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { AnalyticsProvider } from '../../common/AnalyticsProvider';
import { getPage, PAGE_SIZE_LIMIT } from '../../services/confluence';
import { Errors, TreeStates } from '../../types';
import type { PageTreeItem, PageTreeItemProperties } from '../../types';
import { useDraftsToggle } from '../../controllers/drafts-toggle';
import { type ConfluencePageTreeProviderProps, EventName } from './types';
import { ConfluenceTableTreeData, getPageTreeItemProperties } from './confluence-table-tree-data';
import { ConfluencePageTreeContext } from './confluence-page-tree-context';
import {
	fetchPublishedPageTreeItems,
	fetchDraftsPageTreeItems,
	updatePublishedPageTreeItems,
	firePageTreeOperationalEvents,
} from './utils';

export const confluenceTableTreeData = new ConfluenceTableTreeData();

export const ConfluencePageTreeContextProvider = ({
	onError,
	accountId,
	children,
}: Omit<ConfluencePageTreeProviderProps, 'analyticsClient'>) => {
	const [items, setItems] = useState<PageTreeItem[] | null>(null);
	const [errorType, setErrorType] = useState<Errors | null>(null);
	const [spaceKey, setSpaceKey] = useState<string | null>(null);
	const [rootContentId, setRootContentId] = useState<string | null>(null);
	const [canShowMorePages, setCanShowMorePages] = useState(false);
	const [currentLoadMoreCursor, setCurrentLoadMoreCursor] = useState(0);
	const [projectId, setProjectId] = useState<number | undefined>(0);
	const [projectType, setProjectType] = useState<string | undefined>('');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [
		{ isDraftsShown, isTreeUpdatedForDrafts },
		{ loadIsDraftsShownPreference, setIsTreeUpdatedForDrafts },
	] = useDraftsToggle();

	const setContent = useCallback((spaceKey: string, rootContentId?: string) => {
		setSpaceKey(spaceKey);
		setRootContentId(rootContentId || null);
	}, []);

	const setProjectData = useCallback((projectId?: number, projectType?: string) => {
		setProjectId(projectId);
		setProjectType(projectType);
	}, []);

	// use this instead of setItems() that may affect root items to ensure Empty state is updated appropriately
	const updateTree = useCallback((items: PageTreeItem[], isRoot: boolean) => {
		if (isRoot) {
			setErrorType(items.length > 0 ? null : Errors.Empty);
		}
		setItems(items);
	}, []);

	const fetchPublishedPagesAndUpdateSubtree = useCallback(
		async (parentContentId: string | null, start: number) => {
			const isRoot = parentContentId === rootContentId;

			const response = await fetchPublishedPageTreeItems(
				spaceKey,
				parentContentId,
				isRoot,
				start,
				confluenceTableTreeData,
				projectId,
				projectType,
			);

			let treeItems = response.pageTreeItems;

			if (isDraftsShown) {
				// Inject drafts page tree into the current page tree
				treeItems = await fetchDraftsPageTreeItems(
					spaceKey,
					parentContentId,
					confluenceTableTreeData,
					projectId,
					projectType,
				);
			}

			setCanShowMorePages(Boolean(response.next));

			updateTree(treeItems, isRoot);
			firePageTreeOperationalEvents({
				eventName: EventName.SHOWN,
				analyticsAttributes: { pageCount: treeItems.length, isRoot },
				createAnalyticsEvent,
			});
		},
		[
			rootContentId,
			spaceKey,
			isDraftsShown,
			updateTree,
			createAnalyticsEvent,
			projectId,
			projectType,
		],
	);

	const updatePublishedPagesAndSubtree = useCallback(
		async ({
			parentContentId,
			start,
			maxPages,
		}: {
			parentContentId: string | null;
			start: number;
			maxPages?: number;
		}) => {
			const isRoot = parentContentId === rootContentId;

			let treeItems = await updatePublishedPageTreeItems(
				spaceKey,
				parentContentId,
				isRoot,
				start,
				confluenceTableTreeData,
				maxPages,
				projectId,
				projectType,
			);

			if (isDraftsShown) {
				// Inject drafts page tree into the current page tree
				treeItems = await fetchDraftsPageTreeItems(
					spaceKey,
					parentContentId,
					confluenceTableTreeData,
					projectId,
					projectType,
				);
			}

			updateTree(treeItems, isRoot);
			firePageTreeOperationalEvents({
				eventName: EventName.SHOWN,
				analyticsAttributes: { pageCount: treeItems.length, isRoot },
				createAnalyticsEvent,
			});
		},
		[
			rootContentId,
			isDraftsShown,
			spaceKey,
			updateTree,
			createAnalyticsEvent,
			projectId,
			projectType,
		],
	);

	const fetchRootTree = useCallback(async () => {
		if (rootContentId === null && spaceKey === null) {
			return;
		}
		try {
			await fetchPublishedPagesAndUpdateSubtree(rootContentId, 0);
			setIsTreeUpdatedForDrafts(true);
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			const newErrorState = message.includes('404') ? Errors.Empty : Errors.Error;
			const error = err as Error;
			firePageTreeOperationalEvents({
				eventName: EventName.FAILED,
				analyticsAttributes: {
					error: typeof error === 'string' ? 'Failed to fetch' : String(error.message),
					isRoot: true,
				},
				createAnalyticsEvent,
			});
			setErrorType(newErrorState);
		}
	}, [
		rootContentId,
		fetchPublishedPagesAndUpdateSubtree,
		setIsTreeUpdatedForDrafts,
		spaceKey,
		createAnalyticsEvent,
	]);

	const expandSubtree = useCallback(
		async (parentContentId: string) => {
			// expand the subtree from the start to reveal the spinner row first
			setItems(confluenceTableTreeData.setIsExpanded(parentContentId, true));
			try {
				await updatePublishedPagesAndSubtree({ parentContentId, start: 0 });
			} catch (error: any) {
				firePageTreeOperationalEvents({
					eventName: EventName.FAILED,
					analyticsAttributes: {
						error: typeof error === 'string' ? 'Failed to fetch' : String(error.message),
						isRoot: false,
					},
					createAnalyticsEvent,
				});
				setItems(confluenceTableTreeData.markChildError(parentContentId));
				onError?.(error);
			}
			// collapse & prune the subtree if in the end we found no children
			if (confluenceTableTreeData.getPage(parentContentId)?.children === undefined) {
				setItems(confluenceTableTreeData.pruneSubtree(parentContentId));
			}
		},
		[updatePublishedPagesAndSubtree, onError, createAnalyticsEvent],
	);

	const collapseSubtree = useCallback((parentContentId: string) => {
		setItems(confluenceTableTreeData.setIsExpanded(parentContentId, false));
	}, []);

	const updatePage = useCallback(
		async (
			contentId: string,
			status: 'draft' | 'current',
		): Promise<PageTreeItemProperties | undefined> => {
			try {
				const page = await getPage(contentId, status, projectId, projectType);
				if (page.status === 'current' || (page.status === 'draft' && isDraftsShown)) {
					setItems(confluenceTableTreeData.injectPages([page]));
				}
				return getPageTreeItemProperties(page);
			} catch (error: any) {
				firePageTreeOperationalEvents({
					eventName: EventName.FAILED,
					analyticsAttributes: {
						error: typeof error === 'string' ? 'Failed to fetch' : String(error.message),
						isRoot: false,
					},
					createAnalyticsEvent,
				});
				onError?.(error);
			}
		},
		[isDraftsShown, onError, createAnalyticsEvent, projectId, projectType],
	);

	const insertPage = useCallback(
		async (
			contentId: string,
			parentContentId?: string,
		): Promise<PageTreeItemProperties | undefined> => {
			try {
				const isRoot = parentContentId === undefined || parentContentId === rootContentId;

				// when inserting a page, we first try to fetch the latest tree
				await updatePublishedPagesAndSubtree({
					parentContentId: parentContentId || rootContentId,
					start: 0,
					// With "show more", we only want to fetch the pages til reaching the ones shown currently
					// We want to add this max cap only if "show more" FF enabled and the fetching is targeting on root level
					maxPages: isRoot ? currentLoadMoreCursor + PAGE_SIZE_LIMIT : undefined,
				});
				// this subtree may not yet contain the new page, so we fetch it directly as well
				const page = await getPage(contentId, 'draft', projectId, projectType);
				// and inject it into the tree
				if (isDraftsShown) {
					updateTree(confluenceTableTreeData.injectPages([page]), isRoot);
				}
				return getPageTreeItemProperties(page);
			} catch (error: any) {
				firePageTreeOperationalEvents({
					eventName: EventName.FAILED,
					analyticsAttributes: {
						error: typeof error === 'string' ? 'Failed to fetch' : String(error.message),
						isRoot: parentContentId === undefined,
					},
					createAnalyticsEvent,
				});
				onError?.(error);
			}
		},
		[
			updatePublishedPagesAndSubtree,
			isDraftsShown,
			onError,
			rootContentId,
			updateTree,
			currentLoadMoreCursor,
			createAnalyticsEvent,
			projectId,
			projectType,
		],
	);

	const removePage = useCallback(
		async (contentId: string) => {
			const page = confluenceTableTreeData.getPage(contentId);
			const isRoot = page?.parent === undefined;

			try {
				// try to update the tree, which may reflect the latest changes like child pages shifting up to parent
				await updatePublishedPagesAndSubtree({
					parentContentId: page?.parent?.id || rootContentId,
					start: 0,
					// With "show more", we only want to fetch the pages til reaching the ones shown currently
					// We want to add this max cap only if "show more" FF enabled and the fetching is targeting on root level
					maxPages: isRoot ? currentLoadMoreCursor + PAGE_SIZE_LIMIT : undefined,
				});
				// forcibly remove page from tree if for some reason it is still in the tree
				updateTree(confluenceTableTreeData.removePage(contentId), page?.parent === undefined);
				// collapse & prune the subtree if this was the last child page
				if (page?.parent && page?.parent.children === undefined) {
					setItems(confluenceTableTreeData.pruneSubtree(page.parent.id));
				}
			} catch (error: any) {
				firePageTreeOperationalEvents({
					eventName: EventName.FAILED,
					analyticsAttributes: {
						error: typeof error === 'string' ? 'Failed to fetch' : String(error.message),
						isRoot: page?.parent === undefined,
					},
					createAnalyticsEvent,
				});
				onError?.(error);
			}
		},
		[
			updatePublishedPagesAndSubtree,
			onError,
			rootContentId,
			updateTree,
			currentLoadMoreCursor,
			createAnalyticsEvent,
		],
	);

	const fetchAndShowDraftsPage = useCallback(() => {
		fetchDraftsPageTreeItems(
			spaceKey,
			rootContentId,
			confluenceTableTreeData,
			projectId,
			projectType,
		).then((draftsPageTreeItems) => {
			updateTree(draftsPageTreeItems, true);
			setIsTreeUpdatedForDrafts(true);
		});
	}, [spaceKey, rootContentId, updateTree, setIsTreeUpdatedForDrafts, projectId, projectType]);

	// initialise the drafts toggle preference on mount
	useEffect(() => {
		loadIsDraftsShownPreference(accountId);
	}, [loadIsDraftsShownPreference, accountId]);

	// initial load: load the page tree when we don't have a page tree
	useEffect(() => {
		if (isDraftsShown === null) {
			// don't start fetching until draft setting is loaded
			return;
		}
		if (items === null) {
			fetchRootTree();
		}
	}, [isDraftsShown, items, setIsTreeUpdatedForDrafts, fetchRootTree, projectId, projectType]);

	// handle fetching/purging drafts in page tree when the toggle setting changes
	useEffect(() => {
		if (items === null) {
			return;
		}
		if (isTreeUpdatedForDrafts === false) {
			setIsTreeUpdatedForDrafts(null); // put it in a loading state so we don't retrigger it
			if (isDraftsShown) {
				fetchAndShowDraftsPage();
			} else {
				const updatedTree = confluenceTableTreeData.purgeDrafts();
				if (updatedTree.length === 0) {
					setErrorType(Errors.Empty);
				}
				updateTree(updatedTree, true);
				setIsTreeUpdatedForDrafts(true);
			}
		}
	}, [
		isDraftsShown,
		items,
		isTreeUpdatedForDrafts,
		setIsTreeUpdatedForDrafts,
		updateTree,
		fetchAndShowDraftsPage,
		fetchRootTree,
	]);

	// if spaceKey or contentId changes, reload
	useEffect(() => {
		setErrorType(null);
		setItems(null);
		confluenceTableTreeData.init(rootContentId);
	}, [spaceKey, rootContentId]);

	// tree state is an entirely derived var
	let treeState: TreeStates = TreeStates.Loading;
	if (errorType === Errors.Empty) {
		treeState = TreeStates.Empty;
	} else if (errorType === Errors.Error) {
		treeState = TreeStates.Error;
	} else if (items && items.length > 0) {
		treeState = TreeStates.RenderedChildren;
	}

	return (
		<ConfluencePageTreeContext.Provider
			value={{
				treeState,
				spaceKey,
				errorType,
				rootContentId,
				accountId,
				items,
				setContent,
				expandSubtree,
				collapseSubtree,
				insertPage,
				updatePage,
				removePage,
				canShowMorePages,
				showMorePages: async () => {
					await fetchPublishedPagesAndUpdateSubtree(
						rootContentId,
						currentLoadMoreCursor + PAGE_SIZE_LIMIT,
					);

					setCurrentLoadMoreCursor((prev) => prev + PAGE_SIZE_LIMIT);
				},
				setProjectData,
				projectId,
				projectType,
			}}
		>
			{children}
		</ConfluencePageTreeContext.Provider>
	);
};

export const ConfluencePageTreeProvider = ({
	analyticsClient,
	...props
}: ConfluencePageTreeProviderProps) => (
	<AnalyticsProvider
		// @ts-expect-error - Type 'AnalyticsWebClient' is missing the following properties from type 'AnalyticsWebClient': _apdexEvent, _context, eventDelayQueue, _historyReplaceFn, and 61 more
		analyticsClient={analyticsClient}
	>
		<ConfluencePageTreeContextProvider {...props} />
	</AnalyticsProvider>
);
